import React, { useState } from "react";
import config from "lib/fr-config";
import CommonModalView from "presentational/common/modals/CommonModalView";
import IconButtonView from "presentational/common/IconButtonView";
import FolderNameInputModalView from "presentational/common/modals/FolderNameInputModalView";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemSecondaryAction from "@material-ui/core/ListItemSecondaryAction";
import Divider from "@material-ui/core/Divider";
import { withStyles } from "@material-ui/core/styles";
import TXT from "presentational/common/multinational/TXT";
import PropTypes from "prop-types";

const commonCss = config.css.commonDevice;

const TOP_AND_BOTTOM_LIST_BTN_PADDING =
  commonCss.folderListItemBtn.topAndBottomPaddingOfBtn;
const SIDE_LIST_BTN_PADDING = commonCss.folderListItemBtn.sidePaddingOfBtn;
const RIGHT_ICON_MARGIN = commonCss.folderListItemBtn.rightIconMargin;

const css = {
  paper: {
    marginLeft: 0,
    marginRight: 0,
    width: "100%",
    maxWidth: 300
  }
};

const CustomList = withStyles({
  root: {
    marginLeft: -SIDE_LIST_BTN_PADDING,
    marginRight: -SIDE_LIST_BTN_PADDING,
    padding: 0
  }
})(List);
const CustomListItem = withStyles({
  root: {
    padding: `${TOP_AND_BOTTOM_LIST_BTN_PADDING}px ${SIDE_LIST_BTN_PADDING}px`,
    paddingRight: `${SIDE_LIST_BTN_PADDING + RIGHT_ICON_MARGIN}px`
  }
})(ListItem);
const CustomListItemText = withStyles({
  root: { margin: 0 },
  primary: {
    lineHeight: 1.1
  }
})(ListItemText);
const CustomListItemSecondaryAction = withStyles({
  root: { marginRight: -10 }
})(ListItemSecondaryAction);
const CustomDivider = withStyles({
  root: { marginTop: 10, marginBottom: 10 }
})(Divider);

FolderListModalView.propTypes = {
  type: PropTypes.oneOf(["bookmark", "pick"]).isRequired,
  open: PropTypes.bool.isRequired,
  dataObj: PropTypes.object,
  folderList: PropTypes.array.isRequired,
  onClose: PropTypes.func.isRequired,
  onFolderCreateBtnSubmit: PropTypes.func.isRequired,
  onListButtonClick: PropTypes.func.isRequired
};

function FolderListModalView(props) {
  const {
    type,
    open,
    dataObj,
    folderList,
    onClose,
    onFolderCreateBtnSubmit,
    onListButtonClick
  } = props;
  const [inputModalOpen, setInputModalOpen] = useState(false);
  const [inputFolderName, setInputFolderName] = useState(null);

  const getButtonText = ({ hash, name }) => {
    return hash === "all" ? <TXT name="msg_allOutfitOfDropdown" /> : name;
  };

  const getCheckedFolderList = () => {
    if (!dataObj) return [];

    let isChecked, checkedFolder;
    switch (type) {
      case "bookmark":
        isChecked = dataObj.isBookmarked;
        checkedFolder = dataObj.bookmarkedFolder;
        break;
      case "pick":
        isChecked = dataObj.isPicked;
        checkedFolder = dataObj.pickedFolder;
        break;

      default:
        console.error(`wrong type: ${type}`);
        return [];
    }

    const mainArr = isChecked ? ["all"] : [];
    return mainArr.concat(checkedFolder);
  };

  const getIconType = ({ hash }) => {
    switch (type) {
      case "bookmark":
        return getCheckedFolderList().includes(hash)
          ? "bookmarked"
          : "unbookmarked";
      case "pick":
        return getCheckedFolderList().includes(hash) ? "picked" : "unpicked";

      default:
        console.error(`wrong type: ${type}`);
        return null;
    }
  };

  const getIconColor = ({ hash }) => {
    switch (type) {
      case "bookmark":
        return getCheckedFolderList().includes(hash) ? "primary" : "default";
      case "pick":
        return getCheckedFolderList().includes(hash) ? "forPick" : "default";

      default:
        console.error(`wrong type: ${type}`);
        return null;
    }
  };

  const getFolderListContent = () => {
    return (
      <CustomList>
        {folderList.map((unit, index) => (
          <span key={index}>
            <CustomListItem button>
              <CustomListItemText primary={getButtonText(unit)} />
              <CustomListItemSecondaryAction>
                <IconButtonView
                  type={getIconType(unit)}
                  onClick={handleIconButtonClick(unit)}
                  buttonSize={"small"}
                  fontSize={"medium"}
                  color={getIconColor(unit)}
                />
              </CustomListItemSecondaryAction>
            </CustomListItem>
            {unit.hash === "all" && <CustomDivider />}
          </span>
        ))}
        <CustomListItem button>
          <CustomListItemText primary={<TXT name="btnList_createFolder" />} />
          <CustomListItemSecondaryAction>
            <IconButtonView
              type={"add"}
              onClick={handleFolderAddButtonClick}
              buttonSize={"small"}
              fontSize={"medium"}
              color={"default"}
            />
          </CustomListItemSecondaryAction>
        </CustomListItem>
      </CustomList>
    );
  };

  const getTitleText = () => {
    switch (type) {
      case "bookmark":
        return <TXT name="titleModal_bookmarkList" />;
      case "pick":
        return <TXT name="titleModal_pickList" />;

      default:
        console.error(`wrong type: ${type}`);
        return null;
    }
  };

  const handleIconButtonClick =
    ({ hash, name }) =>
    () => {
      onListButtonClick({ hash, name });
    };

  const handleFolderAddButtonClick = () => {
    onClose();
    setInputModalOpen(true);
  };

  const handleInputModalClose = () => {
    setInputModalOpen(false);
  };

  const handleInputModalChange = value => {
    setInputFolderName(value);
  };

  const handleSubmit = () => {
    onFolderCreateBtnSubmit(inputFolderName);
    setInputFolderName(null);
  };

  return (
    <div>
      <CommonModalView
        open={open}
        onClose={onClose}
        paperStyle={css.paper}
        titleText={getTitleText()}
        contentComponent={getFolderListContent()}
        firstButtonText={<TXT name="btnModal_close" />}
        firstButtonFunc={onClose}
      />
      <FolderNameInputModalView
        open={inputModalOpen}
        value={inputFolderName}
        modalType={"create"}
        folderName={null}
        onClose={handleInputModalClose}
        onChange={handleInputModalChange}
        onSubmit={handleSubmit}
      />
    </div>
  );
}

export default FolderListModalView;
